import { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { get } from '@/utils/request';

interface CreditResponse {
  tenantId: number;
  isFirstLevelCustomer: boolean;
  creditSummary: { state: 'ready' | 'disabled' | 'missing_account' | 'not_applicable' | 'invalid_relationship'; availableYuan?: string };
}

type Display = { eligible: boolean; state: string; amount?: string };

// Parent keys this component by user + tenant; no cross-tenant cache is retained.
export default function HeaderCreditSummary({ tenantId, compact = false }: { tenantId?: number; compact?: boolean }) {
  const [display, setDisplay] = useState<Display>({ eligible: false, state: 'loading' });

  useEffect(() => {
    if (!tenantId) return;
    let disposed = false;
    let controller: AbortController | undefined;
    let timer: ReturnType<typeof setInterval> | undefined;
    let inFlight = false;
    let sequence = 0;

    const refresh = async () => {
      if (disposed || document.visibilityState !== 'visible' || inFlight) return;
      inFlight = true;
      const current = ++sequence;
      controller = new AbortController();
      setDisplay((previous) => ({ eligible: previous.eligible, state: 'loading' }));
      try {
        const config = {
          signal: controller.signal, headers: { 'X-Suppress-Error': 'true' },
        };
        const data = await get<CreditResponse>('/api/v1/finance/fund-accounts/my', { summaryOnly: true }, config);
        if (disposed || current !== sequence) return;
        if (data.tenantId !== tenantId) {
          setDisplay({ eligible: false, state: 'invalid_relationship' });
          return;
        }
        setDisplay({ eligible: data.isFirstLevelCustomer, state: data.creditSummary.state, amount: data.creditSummary.availableYuan });
      } catch {
        if (!disposed && current === sequence) setDisplay((previous) => ({ eligible: previous.eligible, state: 'error' }));
      } finally {
        if (current === sequence) inFlight = false;
      }
    };
    const resume = () => {
      if (timer) clearInterval(timer);
      timer = undefined;
      if (document.visibilityState !== 'visible') {
        ++sequence;
        controller?.abort();
        inFlight = false;
        return;
      }
      void refresh();
      timer = setInterval(() => { void refresh(); }, 60_000);
    };
    const focus = () => { void refresh(); };
    resume();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', focus);
    return () => {
      disposed = true;
      ++sequence;
      controller?.abort();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', focus);
    };
  }, [tenantId]);

  if (!display.eligible) return null;
  const messages: Record<string, string> = { loading: '正在查询', error: '查询失败', missing_account: '未配置资金账户', disabled: '账户停用' };
  const amount = display.state === 'ready' ? display.amount ?? '--' : '--';
  const suffix = display.state === 'disabled' ? '（账户停用）' : '';
  return (
    <Tooltip title={`资金账户当前可用余额（预存款加剩余授信，已扣锁定金额）${messages[display.state] ? `；${messages[display.state]}` : ''}`}>
      <span aria-label="可用余额" style={{ display: 'inline-flex', flexDirection: compact ? 'column' : 'row', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: compact ? 12 : 13, lineHeight: compact ? '16px' : undefined }}>
        <span>可用余额: </span><span>{amount}{suffix}</span>
      </span>
    </Tooltip>
  );
}
